import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
   ResponsiveContainer,
   LineChart,
   Line,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
} from 'recharts';
import PanelLayout from '../layouts/PanelLayout';
import Button from '../../components/common/Button';
import ToolUsagePanel from '../components/ToolUsagePanel';
import { statsService } from '../../services/statsService';
import { blogFeedbackService } from '../../services/blogFeedbackService';
import { formatDate, formatTime } from '../../utils/helpers';

// ── Chart helpers ─────────────────────────────────────────────────────────────

const fmtDate = (d) => {
   if (!d) return '';
   const [, m, day] = d.split('-');
   return `${day}/${m}`;
};

const EmptyChart = () => (
   <div className="flex flex-col items-center justify-center h-[260px] text-gray-400 dark:text-gray-600 gap-2">
      <svg
         className="w-8 h-8"
         fill="none"
         stroke="currentColor"
         strokeWidth={1.5}
         viewBox="0 0 24 24"
      >
         <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.5l5-5 4 4 5-7 4 4" />
      </svg>
      <p className="text-sm">No data for this period</p>
   </div>
);

const ChartCard = ({
   title,
   subtitle,
   presets,
   activePreset,
   customFrom,
   customTo,
   loading,
   onPresetChange,
   onCustomFromChange,
   onCustomToChange,
   onApplyCustom,
   children,
}) => (
   <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
         <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
         </div>
         {/* Filter tabs */}
         <div className="flex items-center gap-1 flex-wrap">
            {presets.map((p) => (
               <button
                  key={p.label}
                  onClick={() => onPresetChange(p.label)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                     activePreset === p.label
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
               >
                  {p.label}
               </button>
            ))}
         </div>
      </div>

      {/* Custom date inputs */}
      {activePreset === 'Custom' && (
         <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">From</span>
            <input
               type="date"
               value={customFrom}
               max={customTo}
               onChange={(e) => onCustomFromChange(e.target.value)}
               className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">To</span>
            <input
               type="date"
               value={customTo}
               min={customFrom}
               onChange={(e) => onCustomToChange(e.target.value)}
               className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
               onClick={onApplyCustom}
               className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
               Apply
            </button>
         </div>
      )}

      {/* Chart area */}
      {loading ? (
         <div className="flex items-center justify-center h-[260px] text-sm text-gray-400 dark:text-gray-500">
            Loading...
         </div>
      ) : (
         children
      )}
   </div>
);

// ── Existing stat card helpers ────────────────────────────────────────────────

const STAT_ACCENTS = {
   green: 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-400',
   yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-400',
   purple: 'bg-purple-100 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400',
   red: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400',
   blue: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
   gray: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// Outline icon paths (24x24 viewBox)
const STAT_ICONS = {
   doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
   pencil: 'M15.232 5.232l3.536 3.536M9 13l6.768-6.768a2.5 2.5 0 013.536 3.536L12.5 16.5 8 18l1-5z',
   folder: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
   tag: 'M7 7h.01M3 5a2 2 0 012-2h5.586a1 1 0 01.707.293l8.414 8.414a1 1 0 010 1.414l-5.586 5.586a1 1 0 01-1.414 0L3.293 10.293A1 1 0 013 9.586V5z',
   user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM4 21a8 8 0 0116 0',
   eye: 'M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z',
   chart: 'M4 19V9m6 10V5m6 14v-7m4 7H2',
   mail: 'M3 8l9 6 9-6M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
   stack: 'M12 3L3 8l9 5 9-5-9-5zM3 13l9 5 9-5',
   check: 'M5 13l4 4L19 7',
   clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
   vote: 'M9 12l2 2 4-4M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z',
   bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
   ban: 'M18.364 5.636a9 9 0 11-12.728 12.728 9 9 0 0112.728-12.728zM5.636 5.636l12.728 12.728',
   pause: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z',
   click: 'M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122',
   inbox: 'M3 13h5l1 3h6l1-3h5M3 13l2-8h14l2 8M3 13v6a2 2 0 002 2h14a2 2 0 002-2v-6',
   link: 'M10 14a4 4 0 005.657 0l3-3a4 4 0 00-5.657-5.657l-1 1M14 10a4 4 0 00-5.657 0l-3 3a4 4 0 005.657 5.657l1-1',
};

const OlStatCard = ({
   label,
   value,
   sub,
   icon = 'chart',
   to,
   color = 'text-gray-900 dark:text-gray-100',
}) => {
   const key = (color.match(/text-(green|yellow|purple|red|blue)-/) || [])[1] || 'gray';
   const classes =
      'flex-1 min-w-[140px] flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2.5 transition-all hover:shadow-sm hover:border-gray-300 dark:hover:border-gray-600';
   const body = (
      <>
         <span
            className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg ${STAT_ACCENTS[key]}`}
         >
            <svg
               className="w-5 h-5"
               fill="none"
               stroke="currentColor"
               strokeWidth={1.8}
               strokeLinecap="round"
               strokeLinejoin="round"
               viewBox="0 0 24 24"
               aria-hidden="true"
            >
               <path d={STAT_ICONS[icon] || STAT_ICONS.chart} />
            </svg>
         </span>
         <span className="min-w-0">
            <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight truncate">
               {label}
            </span>
            <span className={`block text-xl font-bold leading-tight ${color}`}>
               {value === undefined || value === null ? 'N/A' : value.toLocaleString()}
            </span>
            {sub && (
               <span className="block text-[10px] text-gray-400 dark:text-gray-500">{sub}</span>
            )}
         </span>
      </>
   );
   return to ? (
      <Link to={to} className={classes}>
         {body}
      </Link>
   ) : (
      <div className={classes}>{body}</div>
   );
};

const OlSection = ({ title, children }) => (
   <div>
      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
         {title}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
   </div>
);

const TabBar = ({ tabs, active, onChange }) => {
   const scrollRef = useRef(null);
   const [fade, setFade] = useState({ left: false, right: false });

   const updateFade = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setFade({
         left: el.scrollLeft > 4,
         right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
   }, []);

   useEffect(() => {
      updateFade();
      window.addEventListener('resize', updateFade);
      return () => window.removeEventListener('resize', updateFade);
   }, [updateFade]);

   const select = (id) => {
      onChange(id);
      scrollRef.current
         ?.querySelector(`[data-tab="${id}"]`)
         ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
   };

   // Arrow keys / Home / End move between tabs; the focused tab is activated (WAI-ARIA tabs pattern).
   const handleKeyDown = (e) => {
      const i = tabs.findIndex((t) => t.id === active);
      let next;
      if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      else return;
      e.preventDefault();
      select(tabs[next].id);
      scrollRef.current?.querySelector(`[data-tab="${tabs[next].id}"]`)?.focus();
   };

   return (
      // Sticks just under the 4rem (h-16) panel header; the background hides content scrolling beneath.
      <div className="sticky top-16 z-20 bg-gray-100 dark:bg-dark-primary">
         <div className="relative after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gray-200 dark:after:bg-gray-700">
            <div
               ref={scrollRef}
               onScroll={updateFade}
               onKeyDown={handleKeyDown}
               role="tablist"
               aria-label="Dashboard sections"
               className="relative z-10 flex gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
               {tabs.map((t) => {
                  const isActive = active === t.id;
                  return (
                     <button
                        key={t.id}
                        id={`dashboard-tab-${t.id}`}
                        data-tab={t.id}
                        role="tab"
                        type="button"
                        aria-selected={isActive}
                        aria-controls="dashboard-tabpanel"
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => select(t.id)}
                        className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:focus-visible:ring-dark-accent focus-visible:ring-inset rounded-t-md ${
                           isActive
                              ? 'border-blue-600 text-blue-600 dark:border-dark-accent dark:text-dark-accent font-semibold'
                              : 'border-transparent text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                     >
                        {t.label}
                     </button>
                  );
               })}
            </div>
            {fade.left && (
               <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-8 bg-gradient-to-r from-gray-100 dark:from-dark-primary to-transparent" />
            )}
            {fade.right && (
               <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-8 bg-gradient-to-l from-gray-100 dark:from-dark-primary to-transparent" />
            )}
         </div>
      </div>
   );
};

const TABS = [
   { id: 'blogs', label: 'Blogs' },
   { id: 'invites', label: 'Date Invites' },
   { id: 'polls', label: 'Polls' },
   { id: 'contact', label: 'Contact' },
   { id: 'tools', label: 'Tool Usage' },
];

const Dashboard = () => {
   const [tab, setTab] = useState('blogs');
   const [refreshKey, setRefreshKey] = useState(0);
   const [stats, setStats] = useState({
      blogs: { published: 0, draft: 0 },
      categories: 0,
      tags: 0,
      authors: 0,
      contactMessages: 0,
      totalViews: 0,
      avgViews: 0,
      topBlogs: [],
      dateInvites: 0,
      dateInvitesAnswered: 0,
      polls: 0,
      pollVotes: 0,
      tools: { opens: 0, uses: 0, used: 0 },
      updatedAt: null,
   });
   const [loading, setLoading] = useState(true);
   const [refreshing, setRefreshing] = useState(false);
   const [feedbackSummary, setFeedbackSummary] = useState([]);
   const [feedbackLoading, setFeedbackLoading] = useState(true);

   // ── Chart state ──────────────────────────────────────────────
   const PRESETS = [
      { label: 'Today', days: 0 },
      { label: '7 Days', days: 7 },
      { label: '30 Days', days: 30 },
      { label: 'Custom', days: null },
   ];
   const toDateStr = (d) => d.toISOString().slice(0, 10);
   const today = toDateStr(new Date());

   const [pollCreatedPreset, setPollCreatedPreset] = useState('7 Days');
   const [pollCreatedCustomFrom, setPollCreatedCustomFrom] = useState(today);
   const [pollCreatedCustomTo, setPollCreatedCustomTo] = useState(today);
   const [pollCreatedData, setPollCreatedData] = useState([]);
   const [pollCreatedChartLoading, setPollCreatedChartLoading] = useState(false);

   const [inviteChartPreset, setInviteChartPreset] = useState('7 Days');
   const [inviteChartCustomFrom, setInviteChartCustomFrom] = useState(today);
   const [inviteChartCustomTo, setInviteChartCustomTo] = useState(today);
   const [inviteChartData, setInviteChartData] = useState([]);
   const [inviteChartLoading, setInviteChartLoading] = useState(false);

   const getDateRange = (preset, customFrom, customTo) => {
      const now = new Date();
      if (preset === 'Today') {
         return { from: today, to: today };
      } else if (preset === '7 Days') {
         const from = new Date(now);
         from.setDate(from.getDate() - 6);
         return { from: toDateStr(from), to: today };
      } else if (preset === '30 Days') {
         const from = new Date(now);
         from.setDate(from.getDate() - 29);
         return { from: toDateStr(from), to: today };
      } else {
         return { from: customFrom, to: customTo };
      }
   };

   const fetchPollCreatedChart = useCallback(async (preset, customFrom, customTo) => {
      setPollCreatedChartLoading(true);
      try {
         const { from, to } = getDateRange(preset, customFrom, customTo);
         const res = await statsService.getPollsChartData({ from, to });
         setPollCreatedData(res.data);
      } catch {
         setPollCreatedData([]);
      } finally {
         setPollCreatedChartLoading(false);
      }
   }, []); // eslint-disable-line

   const fetchInviteChart = useCallback(async (preset, customFrom, customTo) => {
      setInviteChartLoading(true);
      try {
         const { from, to } = getDateRange(preset, customFrom, customTo);
         const res = await statsService.getDateInvitesChartData({ from, to });
         setInviteChartData(res.data);
      } catch {
         setInviteChartData([]);
      } finally {
         setInviteChartLoading(false);
      }
   }, []); // eslint-disable-line

   const fetchStats = useCallback(async () => {
      try {
         const response = await statsService.getDashboardStats();
         setStats(response.data);
      } catch (error) {
         console.error('Error fetching stats:', error);
      } finally {
         setLoading(false);
      }
   }, []);

   const fetchFeedbackSummary = useCallback(async () => {
      try {
         const res = await blogFeedbackService.getSummary();
         setFeedbackSummary(res.data.data);
      } catch (error) {
         console.error('Error fetching blog feedback summary:', error);
      } finally {
         setFeedbackLoading(false);
      }
   }, []);

   // Recomputes the cached stats on the server, then reloads everything else on the
   // dashboard (feedback, both charts at their current ranges, and the Tool Usage tab).
   const handleRefreshStats = async () => {
      try {
         setRefreshing(true);
         const response = await statsService.refreshDashboardStats();
         setStats(response.data);
         await Promise.all([
            fetchFeedbackSummary(),
            fetchPollCreatedChart(pollCreatedPreset, pollCreatedCustomFrom, pollCreatedCustomTo),
            fetchInviteChart(inviteChartPreset, inviteChartCustomFrom, inviteChartCustomTo),
         ]);
         setRefreshKey((k) => k + 1);
         toast.success('Dashboard refreshed');
      } catch (error) {
         console.error('Error refreshing stats:', error);
         toast.error(error.response?.data?.message || 'Failed to refresh dashboard');
      } finally {
         setRefreshing(false);
      }
   };

   useEffect(() => {
      fetchStats();
      fetchFeedbackSummary();
      fetchPollCreatedChart('7 Days');
      fetchInviteChart('7 Days');
   }, []); // eslint-disable-line

   const totalLikes = feedbackSummary.reduce((sum, f) => sum + f.helpfulCount, 0);
   const totalDislikes = feedbackSummary.reduce((sum, f) => sum + f.notHelpfulCount, 0);
   const topFeedbackBlogs = feedbackSummary.slice(0, 5);

   return (
      <PanelLayout>
         <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
                  {stats.updatedAt && (
                     <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        Stats last updated: {formatDate(stats.updatedAt)} ·{' '}
                        {formatTime(stats.updatedAt)}
                     </p>
                  )}
               </div>
               <Button
                  variant="secondary"
                  onClick={handleRefreshStats}
                  disabled={refreshing}
                  className="inline-flex items-center gap-2"
               >
                  {refreshing && (
                     <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {refreshing ? 'Refreshing...' : 'Refresh'}
               </Button>
            </div>

            {/* Tabs */}
            <TabBar tabs={TABS} active={tab} onChange={setTab} />

            <div
               id="dashboard-tabpanel"
               role="tabpanel"
               aria-labelledby={`dashboard-tab-${tab}`}
               className="space-y-8"
            >
               {tab === 'contact' && (
                  <div className="space-y-4">
                     <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                           Contact
                        </h2>
                        <Link
                           to="/admin/contact-messages"
                           className="text-sm text-blue-600 dark:text-dark-accent hover:underline"
                        >
                           Manage
                        </Link>
                     </div>
                     <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-4">
                        <OlSection title="Messages">
                           <OlStatCard
                              label="Contact Messages"
                              icon="mail"
                              to="/admin/contact-messages"
                              value={loading ? 0 : stats.contactMessages}
                              color="text-blue-600 dark:text-blue-400"
                           />
                        </OlSection>
                     </div>
                  </div>
               )}

               {tab === 'blogs' && (
                  <>
                     <div className="space-y-4">
                        <div className="flex items-center justify-between">
                           <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                              Blog Overview
                           </h2>
                           <Link
                              to="/admin/blogs"
                              className="text-sm text-blue-600 dark:text-dark-accent hover:underline"
                           >
                              Manage Blogs
                           </Link>
                        </div>

                        <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-6">
                           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                              <OlSection title="Posts">
                                 <OlStatCard
                                    label="Published"
                                    icon="doc"
                                    value={loading ? 0 : stats.blogs.published}
                                    color="text-green-600 dark:text-green-400"
                                 />
                                 <OlStatCard
                                    label="Draft"
                                    icon="pencil"
                                    value={loading ? 0 : stats.blogs.draft}
                                    color="text-yellow-600 dark:text-yellow-400"
                                 />
                              </OlSection>

                              <OlSection title="Taxonomy">
                                 <OlStatCard
                                    label="Categories"
                                    icon="folder"
                                    value={loading ? 0 : stats.categories}
                                 />
                                 <OlStatCard
                                    label="Tags"
                                    icon="tag"
                                    value={loading ? 0 : stats.tags}
                                 />
                                 <OlStatCard
                                    label="Authors"
                                    icon="user"
                                    value={loading ? 0 : stats.authors}
                                 />
                              </OlSection>

                              <OlSection title="Engagement">
                                 <OlStatCard
                                    label="Total Views"
                                    icon="eye"
                                    value={loading ? 0 : stats.totalViews}
                                    color="text-purple-600 dark:text-purple-400"
                                 />
                                 <OlStatCard
                                    label="Avg Views"
                                    icon="chart"
                                    value={loading ? 0 : stats.avgViews}
                                    color="text-purple-600 dark:text-purple-400"
                                 />
                              </OlSection>
                           </div>
                        </div>

                        {/* Top viewed blogs */}
                        <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-6">
                           <div className="flex items-center justify-between mb-4">
                              <div>
                                 <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                    Top Viewed Blogs
                                 </h3>
                                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Avg {loading ? 0 : (stats.avgViews ?? 0).toLocaleString()} views
                                    per published blog
                                 </p>
                              </div>
                              <Link
                                 to="/admin/blogs"
                                 className="text-sm text-blue-600 dark:text-dark-accent hover:underline"
                              >
                                 View all
                              </Link>
                           </div>
                           {loading ? (
                              <p className="text-sm text-gray-400 dark:text-gray-500">Loading...</p>
                           ) : !stats.topBlogs?.length ? (
                              <p className="text-sm text-gray-400 dark:text-gray-500">
                                 No blogs yet
                              </p>
                           ) : (
                              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                 {stats.topBlogs.map((blog, i) => (
                                    <li key={blog.id} className="flex items-center gap-3 py-2.5">
                                       <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-5">
                                          {i + 1}
                                       </span>
                                       <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">
                                          {blog.title}
                                       </span>
                                       <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 shrink-0">
                                          {(blog.viewCount ?? 0).toLocaleString()}
                                       </span>
                                    </li>
                                 ))}
                              </ul>
                           )}
                        </div>

                        {/* Blog Feedback */}
                        <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-6">
                           <div className="flex items-center justify-between mb-4">
                              <div>
                                 <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                    Blog Feedback
                                 </h3>
                                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Reader "helpful" votes across all posts
                                 </p>
                              </div>
                              <div className="flex items-center gap-6">
                                 <div className="text-center">
                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                       {feedbackLoading ? '0' : totalLikes.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                       Likes
                                    </p>
                                 </div>
                                 <div className="text-center">
                                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                       {feedbackLoading ? '0' : totalDislikes.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                       Dislikes
                                    </p>
                                 </div>
                              </div>
                           </div>
                           {feedbackLoading ? (
                              <p className="text-sm text-gray-400 dark:text-gray-500">Loading...</p>
                           ) : !topFeedbackBlogs.length ? (
                              <p className="text-sm text-gray-400 dark:text-gray-500">
                                 No feedback yet
                              </p>
                           ) : (
                              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                 {topFeedbackBlogs.map((entry, i) => (
                                    <li
                                       key={entry.blog.id}
                                       className="flex items-center gap-3 py-2.5"
                                    >
                                       <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-5">
                                          {i + 1}
                                       </span>
                                       <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">
                                          {entry.blog.title}
                                       </span>
                                       <span className="text-sm font-semibold text-green-600 dark:text-green-400 shrink-0">
                                          +{entry.helpfulCount.toLocaleString()}
                                       </span>
                                       <span className="text-sm font-semibold text-red-600 dark:text-red-400 shrink-0">
                                          -{entry.notHelpfulCount.toLocaleString()}
                                       </span>
                                    </li>
                                 ))}
                              </ul>
                           )}
                        </div>
                     </div>
                  </>
               )}

               {tab === 'invites' && (
                  <>
                     <div className="space-y-4">
                        <div className="flex items-center justify-between">
                           <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                              Date Invites
                           </h2>
                           <Link
                              to="/admin/date-invites"
                              className="text-sm text-blue-600 dark:text-dark-accent hover:underline"
                           >
                              Manage
                           </Link>
                        </div>

                        <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-4">
                           <OlSection title="Date Invites">
                              <OlStatCard
                                 label="Total"
                                 icon="stack"
                                 to="/admin/date-invites"
                                 value={loading ? 0 : stats.dateInvites}
                              />
                              <OlStatCard
                                 label="Answered"
                                 icon="check"
                                 to="/admin/date-invites"
                                 value={loading ? 0 : stats.dateInvitesAnswered}
                                 color="text-green-600 dark:text-green-400"
                              />
                              <OlStatCard
                                 label="Pending"
                                 icon="clock"
                                 to="/admin/date-invites"
                                 value={loading ? 0 : stats.dateInvites - stats.dateInvitesAnswered}
                                 color="text-yellow-600 dark:text-yellow-400"
                              />
                           </OlSection>
                        </div>

                        <ChartCard
                           title="Date Invites"
                           subtitle="Invites created and answered over time"
                           presets={PRESETS}
                           activePreset={inviteChartPreset}
                           customFrom={inviteChartCustomFrom}
                           customTo={inviteChartCustomTo}
                           loading={inviteChartLoading}
                           onPresetChange={(p) => {
                              setInviteChartPreset(p);
                              if (p !== 'Custom') fetchInviteChart(p);
                           }}
                           onCustomFromChange={setInviteChartCustomFrom}
                           onCustomToChange={setInviteChartCustomTo}
                           onApplyCustom={() =>
                              fetchInviteChart('Custom', inviteChartCustomFrom, inviteChartCustomTo)
                           }
                        >
                           {inviteChartData.length === 0 ? (
                              <EmptyChart />
                           ) : (
                              <ResponsiveContainer width="100%" height={260}>
                                 <LineChart
                                    data={inviteChartData}
                                    margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                                 >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                       dataKey="date"
                                       tick={{ fontSize: 11 }}
                                       tickFormatter={fmtDate}
                                    />
                                    <YAxis
                                       allowDecimals={false}
                                       tick={{ fontSize: 11 }}
                                       width={32}
                                    />
                                    <Tooltip labelFormatter={fmtDate} />
                                    <Legend />
                                    <Line
                                       type="monotone"
                                       dataKey="created"
                                       name="Created"
                                       stroke="#ec4899"
                                       strokeWidth={2}
                                       dot={false}
                                       activeDot={{ r: 4 }}
                                    />
                                    <Line
                                       type="monotone"
                                       dataKey="answered"
                                       name="Answered"
                                       stroke="#16a34a"
                                       strokeWidth={2}
                                       dot={false}
                                       activeDot={{ r: 4 }}
                                    />
                                 </LineChart>
                              </ResponsiveContainer>
                           )}
                        </ChartCard>
                     </div>
                  </>
               )}

               {tab === 'polls' && (
                  <>
                     <div className="space-y-4">
                        <div className="flex items-center justify-between">
                           <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                              Polls
                           </h2>
                           <Link
                              to="/admin/polls"
                              className="text-sm text-blue-600 dark:text-dark-accent hover:underline"
                           >
                              Manage
                           </Link>
                        </div>

                        <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-4">
                           <OlSection title="Polls">
                              <OlStatCard
                                 label="Total Polls"
                                 icon="vote"
                                 to="/admin/polls"
                                 value={loading ? 0 : stats.polls}
                              />
                              <OlStatCard
                                 label="Total Votes"
                                 icon="chart"
                                 to="/admin/polls"
                                 value={loading ? 0 : stats.pollVotes}
                                 color="text-purple-600 dark:text-purple-400"
                              />
                           </OlSection>
                        </div>

                        <ChartCard
                           title="Polls Created"
                           subtitle='New "Would You Rather" polls over time'
                           presets={PRESETS}
                           activePreset={pollCreatedPreset}
                           customFrom={pollCreatedCustomFrom}
                           customTo={pollCreatedCustomTo}
                           loading={pollCreatedChartLoading}
                           onPresetChange={(p) => {
                              setPollCreatedPreset(p);
                              if (p !== 'Custom') fetchPollCreatedChart(p);
                           }}
                           onCustomFromChange={setPollCreatedCustomFrom}
                           onCustomToChange={setPollCreatedCustomTo}
                           onApplyCustom={() =>
                              fetchPollCreatedChart(
                                 'Custom',
                                 pollCreatedCustomFrom,
                                 pollCreatedCustomTo
                              )
                           }
                        >
                           {pollCreatedData.length === 0 ? (
                              <EmptyChart />
                           ) : (
                              <ResponsiveContainer width="100%" height={260}>
                                 <LineChart
                                    data={pollCreatedData}
                                    margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                                 >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                       dataKey="date"
                                       tick={{ fontSize: 11 }}
                                       tickFormatter={fmtDate}
                                    />
                                    <YAxis
                                       allowDecimals={false}
                                       tick={{ fontSize: 11 }}
                                       width={32}
                                    />
                                    <Tooltip
                                       formatter={(v) => [v, 'Polls created']}
                                       labelFormatter={fmtDate}
                                    />
                                    <Line
                                       type="monotone"
                                       dataKey="count"
                                       name="Polls created"
                                       stroke="#f97316"
                                       strokeWidth={2}
                                       dot={false}
                                       activeDot={{ r: 4 }}
                                    />
                                 </LineChart>
                              </ResponsiveContainer>
                           )}
                        </ChartCard>
                     </div>
                  </>
               )}

               {tab === 'tools' && (
                  <ToolUsagePanel refreshKey={refreshKey} allTime={stats.tools} loading={loading} />
               )}
            </div>
         </div>
      </PanelLayout>
   );
};

export default Dashboard;
